import React, { useEffect, useMemo, useState } from "react"

import { useNavigate, useSearchParams } from "react-router-dom"

import { PoliciesListType } from "@common-shared/config/oneVerify/oneVerifyPolicies.config.ts"
import {
    IPoliciesListFiltersData,
    IPolicyColumnConfiguration,
} from "@common-shared/types/policies.types.ts"
import { IOneVerifyCoverageCheckHistory } from "@common-shared/types/responseData/oneVerifyResponseData.types.ts"

import { NIRVANA_COLORS } from "@front-shared/config/theme.config.ts"
import { PrimaryText } from "@front-shared/elements/primaryText.element.tsx"

import { GENERAL_CONFIG } from "../../../config/general.config"
import { POLICIES_CONFIG } from "../../../config/policies.config"
import { PoliciesListHelper } from "../../../helpers/policiesList.helper"
import { useScrollableElementBoundaries } from "../../../hooks/scrollableElementBoundaries.hook"
import { useAppDispatch } from "../../../store/appDispatch.hook"
import { useAppSelector } from "../../../store/selectors/app.selector"
import { organizationSelector } from "../../../store/selectors/organization.selector"
import { policiesSelector } from "../../../store/selectors/policies.selector"
import { selectedPracticeRoleSelector } from "../../../store/selectors/selectedPracticeRole.selector"
import {
    policiesSetAgenticFilters,
    policiesSetAssignedTasksFilters,
    policiesSetHistoryFilters,
    policiesSetMedicaidFilters,
    policiesSetOverridesFilters,
    policiesSetPlanYearResetsFilters,
    policiesSetReportsFilters,
    policiesSetTaskAssignmentsFilters,
} from "../../../store/slices/policies.slice"
import { organizationUsersGetList } from "../../../store/thunks/organization.thunks"
import { PoliciesFilterPopupComponent } from "../../popups/policiesFilterPopup.component"
import { PoliciesFiltersDisplayComponent } from "../filters/policiesFiltersDisplay.component"
import { PoliciesColumnsManagementComponent } from "./policiesColumnsManagement.component"
import { PoliciesExportListComponent } from "./policiesExportList.component"

export const PoliciesListHeaderComponent: React.FunctionComponent<
    IPoliciesListHeaderComponentProps
> = props => {
    const navigate = useNavigate()
    const dispatch = useAppDispatch()

    const [searchParams, setSearchParams] = useSearchParams()

    const scrollableTableBoundaries = useScrollableElementBoundaries(
        props.scrollableTableRef,
    )

    const selectedPracticeRole = useAppSelector(selectedPracticeRoleSelector)
    const organizationState = useAppSelector(organizationSelector)

    const policiesState = useAppSelector(policiesSelector)

    const [isFiltersPopupActive, setIsFiltersPopupActive] =
        useState<boolean>(false)

    // Fetch organization users for assignee/assigner/checkedByUsers filters
    // This is needed for task list types and reports/medicaid
    useEffect(() => {
        if (
            !selectedPracticeRole ||
            organizationState.users.length ||
            !(
                POLICIES_CONFIG.tasksListTypes.includes(props.listType) ||
                props.listType === PoliciesListType.REPORTS ||
                props.listType === PoliciesListType.MEDICAID
            )
        ) {
            return
        }

        dispatch(
            organizationUsersGetList({
                payload: undefined,
                practice: selectedPracticeRole.practice,
            }),
        )
    }, [
        selectedPracticeRole?.practice.id,
        props.listType,
        organizationState.users,
    ])

    const {
        policyItems: typeBasedItems,
        nextPagination: typeBasedPagination,
        filtersConfiguration: typeBasedFilters,
    } = useMemo(() => {
        return PoliciesListHelper.getViewTypedStateData(
            props.listType,
            policiesState,
        )
    }, [props.listType, policiesState])

    const filtersCount = useMemo<number>(() => {
        return PoliciesListHelper.getMappedDisplayFilters(
            typeBasedFilters,
            props.listType,
            organizationState.users,
        ).length
    }, [typeBasedFilters, props.listType, organizationState.users])

    // Monitor filters in search params and set them in state
    useEffect(() => {
        const searchParamsFilters = searchParams.get(
            GENERAL_CONFIG.urlSearchParamsKeys.filters,
        )

        // If no reports filters by in url (that's our main reports url)
        // Set them in search params so clicking back works fine
        if (!searchParamsFilters) {
            if (props.ignoreFiltersPersistence) {
                return
            }

            searchParams.set(
                GENERAL_CONFIG.urlSearchParamsKeys.filters,
                JSON.stringify(typeBasedFilters),
            )

            setSearchParams(searchParams)

            return
        }

        // In case json is invalid
        try {
            const filtersFromUrl: IPoliciesListFiltersData =
                JSON.parse(searchParamsFilters)

            if (
                JSON.stringify(filtersFromUrl) ===
                JSON.stringify(typeBasedFilters)
            ) {
                return
            }

            switch (props.listType) {
                case PoliciesListType.HISTORY_SEARCH:
                    dispatch(policiesSetHistoryFilters(filtersFromUrl))

                    break

                case PoliciesListType.REPORTS:
                    dispatch(policiesSetReportsFilters(filtersFromUrl))

                    break

                case PoliciesListType.OVERRIDES:
                    dispatch(policiesSetOverridesFilters(filtersFromUrl))

                    break

                case PoliciesListType.MEDICAID:
                    dispatch(policiesSetMedicaidFilters(filtersFromUrl))

                    break

                case PoliciesListType.PLAN_YEAR_RESETS:
                    dispatch(policiesSetPlanYearResetsFilters(filtersFromUrl))

                    break

                case PoliciesListType.AGENTIC:
                    dispatch(policiesSetAgenticFilters(filtersFromUrl))

                    break

                case PoliciesListType.ASSIGNED_TASKS:
                case PoliciesListType.ASSIGNED_TASKS_EXTENDED_CUSTOM_FIELDS:
                case PoliciesListType.ASSIGNED_TASKS_MEDICAID:
                    dispatch(policiesSetAssignedTasksFilters(filtersFromUrl))

                    break

                case PoliciesListType.TASK_ASSIGNMENTS:
                case PoliciesListType.TASK_ASSIGNMENTS_EXTENDED_CUSTOM_FIELDS:
                case PoliciesListType.TASK_ASSIGNMENTS_MEDICAID:
                    dispatch(policiesSetTaskAssignmentsFilters(filtersFromUrl))

                    break
            }
        } catch (e) {}
    }, [searchParams])

    const onEditFilters = () => {
        if (props.onEditFilters) {
            props.onEditFilters()
        } else {
            setIsFiltersPopupActive(true)
        }
    }

    const onFiltersSubmitted = (data: IPoliciesListFiltersData) => {
        // Deselect everything on filters change
        props.setSelectedPolicies?.([])

        const searchParams = new URLSearchParams()

        searchParams.set(
            GENERAL_CONFIG.urlSearchParamsKeys.filters,
            JSON.stringify(data),
        )

        navigate(
            `${PoliciesListHelper.getPoliciesListBaseUrl(props.listType)}?${searchParams.toString()}`,
        )
    }

    return (
        <div
            className={`relative px-[24px] py-[12px] ${props.className}`}
            style={{
                right: scrollableTableBoundaries?.x,
            }}
        >
            <div
                className="
                    flex items-start
                    md:block
                "
            >
                <PrimaryText
                    className="mr-[16px] mt-[4px] md:mt-0" // Margin top helps for centering text
                    size={29}
                    lineHeight={35}
                    weight="semibold"
                >
                    {POLICIES_CONFIG.headerTitleMapping[props.listType]}
                </PrimaryText>

                {/*filters btn*/}
                <div
                    className="
                        cursor-pointer rounded-[22px] px-[16px] py-[13px] bg-brand-warmLight
                        flex items-center justify-center
                        border border-solid border-brand-primary
                        hover:bg-brand-lilacLight
                    "
                    onClick={onEditFilters}
                >
                    <PrimaryText
                        typography="buttonText"
                        className="relative top-[2px]"
                    >
                        Filters
                    </PrimaryText>

                    {filtersCount ? (
                        <PrimaryText
                            className="ml-[8px] rounded-full bg-brand-primary w-[16px] h-[16px] flex items-center justify-center"
                            size={13}
                            lineHeight={13}
                            weight="bold"
                            color={NIRVANA_COLORS.brand.warmLight}
                        >
                            {filtersCount}
                        </PrimaryText>
                    ) : null}
                </div>

                <div className="flex-1 mx-[16px] md:hidden" />

                <div className="flex items-start md:mt-[32px] md:justify-center">
                    {props.columnsConfiguration.length &&
                    selectedPracticeRole ? (
                        <>
                            <PoliciesColumnsManagementComponent
                                className="ml-[16px]"
                                initialColumnsConfiguration={PoliciesListHelper.getColumnsConfiguration(
                                    props.listType,
                                    selectedPracticeRole.availableModalities,
                                    false,
                                    false,
                                )}
                                currentColumnsConfiguration={
                                    props.columnsConfiguration
                                }
                                onColumnsConfigurationChange={
                                    props.onColumnsConfigurationChange
                                }
                                isRightPlacement
                            />
                        </>
                    ) : null}

                    {props.withExport ? (
                        <PoliciesExportListComponent
                            className="ml-[16px]"
                            listType={props.listType}
                            isDisabled={!typeBasedItems.length}
                            currentPaginationTotal={typeBasedPagination.total}
                        />
                    ) : null}
                </div>
            </div>

            <PoliciesFiltersDisplayComponent
                className="md:mt-[8px]"
                filters={typeBasedFilters}
                listType={props.listType}
                hideRemoveFilterButtons={
                    props.listType === PoliciesListType.HISTORY_SEARCH
                }
            />

            {isFiltersPopupActive && (
                <PoliciesFilterPopupComponent
                    isActive
                    listType={props.listType}
                    existingFilters={typeBasedFilters}
                    ignoreFiltersPersistence={props.ignoreFiltersPersistence}
                    onFiltersSubmitted={onFiltersSubmitted}
                    onDone={() => {
                        setIsFiltersPopupActive(false)
                    }}
                    withSavingFilterPresets={props.withSavingFilterPresets}
                />
            )}
        </div>
    )
}

interface IPoliciesListHeaderComponentProps {
    className?: string
    scrollableTableRef?: React.RefObject<HTMLDivElement>
    listType: PoliciesListType
    setSelectedPolicies?: (items: IOneVerifyCoverageCheckHistory[]) => void
    withExport?: boolean
    withSavingFilterPresets?: boolean
    ignoreFiltersPersistence?: boolean
    onEditFilters?: () => void
    columnsConfiguration: IPolicyColumnConfiguration[]
    onColumnsConfigurationChange: (
        configuration: IPolicyColumnConfiguration[],
    ) => void
}
